'use client'

import { useEffect, ReactNode } from 'react'
import { usePageHeader } from '@/lib/contexts/page-header-context'

/**
 * Hook to set the page header (title, subtitle, actions)
 * This should be called in page components to set their header content
 *
 * IMPORTANT: When passing actions with dynamic content, wrap them in useMemo
 * to prevent infinite re-renders.
 *
 * @example
 * ```tsx
 * const headerActions = useMemo(() => (
 *   <Button>Add Item</Button>
 * ), [dependencies])
 *
 * useSetPageHeader({
 *   title: "Suppliers",
 *   subtitle: "Manage supplier businesses",
 *   actions: headerActions
 * })
 * ```
 */
export function useSetPageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  const { setPageHeader } = usePageHeader()

  useEffect(() => {
    setPageHeader(title, subtitle, actions)
  }, [title, subtitle, actions, setPageHeader])

  // Clear header on unmount
  useEffect(() => {
    return () => {
      setPageHeader('', undefined, undefined)
    }
  }, [])
}

